import { z } from "zod";

import { REQUIRED_MESSAGE } from "@/utils";

import { EProductType } from "../products.type";
import { baseProductSchema } from "./base-product.schema";

export const planAdjustmentTypeSchema = z.enum(["lmi", "is"]);

export const planAdjustmentEntrySchema = z.object({
  adjustment_type: planAdjustmentTypeSchema,
  custom_label: z.string().optional(),
  new_product_value_enabled: z.boolean().optional(),
  new_product_value: z.coerce.number().optional(),
  new_commission_value_enabled: z.boolean().optional(),
  new_commission_value: z.coerce.number().optional(),
  assistance_description: z.string().optional(),
  /** Percentual sobre tabela FIPE quando o ajuste é LMI. */
  fipe_table_percent: z.coerce.number().optional(),
});

export type TPlanAdjustmentType = z.infer<typeof planAdjustmentTypeSchema>;

/** Ordem fixa de exibição / toggles (alinhado à planilha). */
export const PLAN_ADJUSTMENT_TYPES: TPlanAdjustmentType[] = ["lmi", "is"];

export const vehicleValueModeSchema = z.enum(["percentage", "real_currency"]);

export const vehicleCommercialSchema = z.object({
  commission_enabled: z.boolean().default(false),
  commission_mode: vehicleValueModeSchema.optional(),
  commission_value: z.coerce.number().optional(),
  pro_labore_enabled: z.boolean().default(false),
  pro_labore_mode: vehicleValueModeSchema.optional(),
  pro_labore_value: z.coerce.number().optional(),
  markup_enabled: z.boolean().default(false),
  markup_mode: z.enum(["percentage", "real_currency"]).optional(),
  markup_value: z.coerce.number().optional(),
});

export const vehicleProductConfigSchema = z.object({
  commercial: vehicleCommercialSchema,
});

// Schema para os planos (sem tabela de preços NET/Markup/comissão — planilha Auto/Moto)
export const planSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, { message: "Nome do plano é obrigatório" }),
    description: z.string().optional(),
    new_coverages: z.string().optional(),
    plan_adjustments: z.array(planAdjustmentEntrySchema).optional().default([]),
  })
  .superRefine((plan, ctx) => {
    const list = plan.plan_adjustments ?? [];
    const types = list.map((a) => a.adjustment_type);
    if (new Set(types).size !== types.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["plan_adjustments"],
      });
    }
    list.forEach((adj, i) => {
      if (adj.adjustment_type === "lmi") {
        if (
          adj.fipe_table_percent == null ||
          Number.isNaN(Number(adj.fipe_table_percent))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["plan_adjustments", i, "fipe_table_percent"],
          });
        }
      }
      if (
        adj.adjustment_type === "is" &&
        adj.new_product_value_enabled === true
      ) {
        if (
          adj.new_product_value == null ||
          Number.isNaN(Number(adj.new_product_value))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["plan_adjustments", i, "new_product_value"],
          });
        }
      }
      if (adj.new_commission_value_enabled === true) {
        if (
          adj.new_commission_value == null ||
          Number.isNaN(Number(adj.new_commission_value))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["plan_adjustments", i, "new_commission_value"],
          });
        }
      }
    });
  });

const isVehicleType = (t: EProductType) =>
  t === EProductType.AUTO || t === EProductType.MOTO;

export const autoSchema = baseProductSchema
  .omit({
    min_permitted_age: true,
    max_permitted_age: true,
    capital_ranges: true,
    policy_number: true,
    susep_code: true,
    cod_sub: true,
    policy_start_date: true,
    policy_end_date: true,
    secondary_policy_holder: true,
    stipulant_code: true,
    sub_stipulant_code: true,
    table_code: true,
  })
  .extend({
    policy_number: z.string().optional().nullable(),
    susep_code: z.string().optional().nullable(),
    cod_sub: z.string().optional().nullable(),
    policy_start_date: z.date().optional(),
    policy_end_date: z.date().optional(),
    secondary_policy_holder: z.string().optional().nullable(),
    stipulant_code: z.string().optional().nullable(),
    sub_stipulant_code: z.string().optional().nullable(),
    table_code: z.string().optional().nullable(),
    km_max_permitted: z.coerce.number().optional(),
    min_manufacturing_year: z.coerce.number().optional(),
    policy_holder: z.string().optional().nullable(),
    code_plan: z.string().optional().nullable(),
    plans: z.array(planSchema).optional().default([]),
    vehicle_emission_type: z.enum(["apolice", "bilhete"]).optional(),
    is_collective_policy: z.boolean().optional(),
    has_estipulante: z.boolean().optional(),
    has_sub_estipulante: z.boolean().optional(),
    susep_product_code: z.string().optional(),
    has_km_limit: z.boolean().optional(),
    has_manufacturing_year_limit: z.boolean().optional(),
    max_vehicle_age: z.coerce.number().optional(),
    excluded_risks: z.string().optional(),
    premium_mode: z.enum(["prefixed", "calculated"]).optional(),
    prefixed_premium_value: z.coerce.number().optional(),
    has_multiple_plans: z.boolean().optional(),
    vehicle_product_config: vehicleProductConfigSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (!isVehicleType(data.type)) {
      return;
    }

    if (!data.vehicle_emission_type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["vehicle_emission_type"],
      });
    }

    if (typeof data.has_km_limit !== "boolean") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["has_km_limit"],
      });
    }
    if (typeof data.has_manufacturing_year_limit !== "boolean") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["has_manufacturing_year_limit"],
      });
    }

    if (data.has_km_limit === true) {
      if (data.km_max_permitted == null || Number.isNaN(Number(data.km_max_permitted))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["km_max_permitted"],
        });
      }
    }

    if (data.has_manufacturing_year_limit === true) {
      if (data.max_vehicle_age == null || Number.isNaN(Number(data.max_vehicle_age))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["max_vehicle_age"],
        });
      }
    }

    const susepProd = data.susep_product_code?.trim();
    if (!susepProd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["susep_product_code"],
      });
    }

    if (!data.premium_mode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["premium_mode"],
      });
    } else if (data.premium_mode === "prefixed") {
      if (
        data.prefixed_premium_value == null ||
        Number.isNaN(Number(data.prefixed_premium_value))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["prefixed_premium_value"],
        });
      }
    }

    if (data.has_multiple_plans !== true && data.has_multiple_plans !== false) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["has_multiple_plans"],
      });
    } else if (data.has_multiple_plans === true) {
      if (!data.plans || data.plans.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["plans"],
        });
      }
    }

    const commercial = data.vehicle_product_config?.commercial;
    if (!commercial) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["vehicle_product_config"],
      });
    } else {
      const anyOn =
        commercial.commission_enabled === true ||
        commercial.pro_labore_enabled === true ||
        commercial.markup_enabled === true;
      if (!anyOn) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["vehicle_product_config", "commercial"],
        });
      }
      if (commercial.commission_enabled === true) {
        if (
          commercial.commission_value == null ||
          Number.isNaN(Number(commercial.commission_value))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["vehicle_product_config", "commercial", "commission_value"],
          });
        }
      }
      if (commercial.pro_labore_enabled === true) {
        if (
          commercial.pro_labore_value == null ||
          Number.isNaN(Number(commercial.pro_labore_value))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["vehicle_product_config", "commercial", "pro_labore_value"],
          });
        }
      }
      if (commercial.markup_enabled === true) {
        if (!commercial.markup_mode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["vehicle_product_config", "commercial", "markup_mode"],
          });
        }
        if (
          commercial.markup_value == null ||
          Number.isNaN(Number(commercial.markup_value))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["vehicle_product_config", "commercial", "markup_value"],
          });
        }
      }
    }

    if (data.vehicle_emission_type === "bilhete") {
      return;
    }

    if (data.is_collective_policy !== true && data.is_collective_policy !== false) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["is_collective_policy"],
      });
      return;
    }

    if (data.is_collective_policy !== true) {
      return;
    }

    if (!data.policy_number?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["policy_number"],
      });
    }
    if (!data.policy_start_date || !data.policy_end_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["policy_start_date"],
      });
    }

    if (data.has_estipulante !== true && data.has_estipulante !== false) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["has_estipulante"],
      });
    } else if (data.has_estipulante === true) {
      if (!data.insurance_policy_id?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["insurance_policy_id"],
        });
      }
    }

    if (data.has_sub_estipulante !== true && data.has_sub_estipulante !== false) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["has_sub_estipulante"],
      });
    } else if (data.has_sub_estipulante === true) {
      if (!data.cod_sub?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["cod_sub"],
        });
      }
    }
  });

export type TAutoFormData = z.infer<typeof autoSchema>;
export type TPlan = z.infer<typeof planSchema>;
export type TPlanAdjustmentEntry = z.infer<typeof planAdjustmentEntrySchema>;

const ACTIVE_VEHICLE_PLAN_ADJUSTMENT_TYPES = new Set<string>(["lmi", "is"]);

/** Remove tipos de ajuste obsoletos (API legada) antes de popular o formulário. */
export function filterVehiclePlanAdjustmentsForForm<
  T extends { adjustment_type: string },
>(list: T[] | undefined | null): T[] {
  if (!list?.length) return [];
  return list.filter((a) => ACTIVE_VEHICLE_PLAN_ADJUSTMENT_TYPES.has(a.adjustment_type));
}

export type TVehicleCommercialForm = z.infer<typeof vehicleCommercialSchema>;

export function normalizeVehicleCommercialFromApi(
  commercial: Partial<TVehicleCommercialForm> | undefined | null,
): TVehicleCommercialForm {
  const c = commercial ?? {};
  return {
    commission_enabled: Boolean(c.commission_enabled ?? true),
    commission_mode: c.commission_mode ?? "percentage",
    commission_value:
      c.commission_value !== undefined && c.commission_value !== null
        ? Number(c.commission_value)
        : 0,
    pro_labore_enabled: Boolean(c.pro_labore_enabled ?? false),
    pro_labore_mode: c.pro_labore_mode ?? "real_currency",
    pro_labore_value:
      c.pro_labore_value !== undefined && c.pro_labore_value !== null
        ? Number(c.pro_labore_value)
        : 0,
    markup_enabled: Boolean(c.markup_enabled ?? false),
    markup_mode: c.markup_mode ?? "percentage",
    markup_value:
      c.markup_value !== undefined && c.markup_value !== null
        ? Number(c.markup_value)
        : 0,
  };
}
